import { useCallback, useEffect, useState } from 'react'

type PermissionState = 'granted' | 'denied' | 'prompt' | 'unknown'

interface BrowserPermissions {
  camera: PermissionState
  microphone: PermissionState
  hasPermissions: boolean
}

const POLL_INTERVAL_MS = 1500

/**
 * Uses enumerateDevices() to infer permission state.
 * When permission is granted, device labels are populated; when not, they're empty.
 * This works on Safari where navigator.permissions may be unavailable or unreliable.
 */
async function checkPermissionsViaEnumerateDevices(): Promise<{
  camera: PermissionState
  microphone: PermissionState
}> {
  try {
    const devices = await navigator.mediaDevices.enumerateDevices()
    const videoInputs = devices.filter((d) => d.kind === 'videoinput')
    const audioInputs = devices.filter((d) => d.kind === 'audioinput')

    // Labels are empty until permission is granted (security restriction)
    const hasCameraPermission =
      videoInputs.length === 0 || videoInputs.some((d) => d.label && d.label.trim() !== '')
    const hasMicrophonePermission =
      audioInputs.length === 0 || audioInputs.some((d) => d.label && d.label.trim() !== '')

    return {
      camera: hasCameraPermission ? 'granted' : 'prompt',
      microphone: hasMicrophonePermission ? 'granted' : 'prompt'
    }
  } catch {
    return { camera: 'prompt', microphone: 'prompt' }
  }
}

/**
 * Hook to check browser permissions for camera and microphone
 * Returns the current permission state and whether both are granted
 *
 * Uses navigator.permissions when available (Chrome, Firefox, Safari 16+).
 * Falls back to enumerateDevices() for Safari and older browsers:
 * device labels are populated only after permission is granted.
 * Polls when in 'prompt' state to detect when the user grants without reload.
 */
export function useBrowserPermissions(): BrowserPermissions {
  const [cameraPermission, setCameraPermission] = useState<PermissionState>('granted')
  const [microphonePermission, setMicrophonePermission] = useState<PermissionState>('granted')

  const checkPermissions = useCallback(async () => {
    // Try Permissions API first (Chrome, Firefox, Safari 16+)
    if (navigator.permissions?.query) {
      try {
        const [cameraStatus, microphoneStatus] = await Promise.all([
          navigator.permissions.query({ name: 'camera' as PermissionName }),
          navigator.permissions.query({ name: 'microphone' as PermissionName })
        ])

        setCameraPermission(cameraStatus.state as PermissionState)
        setMicrophonePermission(microphoneStatus.state as PermissionState)

        cameraStatus.onchange = () =>
          setCameraPermission(cameraStatus.state as PermissionState)
        microphoneStatus.onchange = () =>
          setMicrophonePermission(microphoneStatus.state as PermissionState)

        return
      } catch {
        // Permissions API threw (e.g. Safari with unsupported permission name)
      }
    }

    // Fallback: enumerateDevices (Safari, older browsers)
    const { camera, microphone } = await checkPermissionsViaEnumerateDevices()
    setCameraPermission(camera)
    setMicrophonePermission(microphone)
  }, [])

  useEffect(() => {
    void checkPermissions()

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void checkPermissions()
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [checkPermissions])

  // Poll when Permissions API not used and still in prompt state
  // (detects when user grants after getUserMedia, e.g. on Safari)
  useEffect(() => {
    if (cameraPermission === 'granted' && microphonePermission === 'granted') return

    const interval = setInterval(async () => {
      const { camera, microphone } = await checkPermissionsViaEnumerateDevices()
      setCameraPermission(camera)
      setMicrophonePermission(microphone)
    }, POLL_INTERVAL_MS)

    return () => clearInterval(interval)
  }, [cameraPermission, microphonePermission])

  const hasPermissions = cameraPermission === 'granted' && microphonePermission === 'granted'

  return {
    camera: cameraPermission,
    microphone: microphonePermission,
    hasPermissions
  }
}
